import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { useLocale, useTranslations } from "use-intl";
import z from "zod";

import { MailIcon } from "@/components/appIcons";
import { AuthAlternativeMethods } from "@/components/auth-alternative-methods";
import { useAppForm } from "@/components/forms/app-form";
import { Link } from "@/components/link";
import { useSession } from "@/hooks/use-session";
import { setLocale } from "@/i18n/actions";
import { setCurrencyPreference } from "@/i18n/currency-actions";
import { getMessagesForRoute } from "@/i18n/get-messages-for-route";
import { resolveSignInLocale } from "@/i18n/resolveSignInLocale";
import { trackLogin, trackLoginStarted } from "@/lib/analytics";
import { authClient } from "@/lib/auth-client";
import { getRedirectAfterLogin } from "@/lib/redirect-after-login";
import { invalidateSession } from "@/utils/session";
import { normalizeViewerCurrency, type ViewerLocale } from "@garimpa/lib/viewerPreferences";

import Loader from "./loader";
import { Button } from "./ui/button";
import { Label } from "./ui/label";

interface SignInFormProps {
  redirectAfterLogin?: string;
}

export default function SignInForm({ redirectAfterLogin }: SignInFormProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { isPending } = useSession();
  const t = useTranslations("globals.auth");
  const tError = useTranslations("globals.error");
  const currentLocale = useLocale() as ViewerLocale;
  const [verificationRequired, setVerificationRequired] = useState<string | null>(null);
  const [whatsappExpanded, setWhatsAppExpanded] = useState(false);
  const signInSchema = z.object({
    email: z.email(t("invalidEmail")),
    password: z.string().min(8, t("passwordMinLength")),
  });

  const form = useAppForm({
    defaultValues: {
      email: "",
      password: "",
    },
    onSubmit: async ({ value }) => {
      const input = signInSchema.parse(value);
      trackLoginStarted("email", "method");
      const result = await authClient.signIn.email({
        email: input.email,
        password: input.password,
      });

      if (result.error) {
        if (result.error.status === 403) {
          setVerificationRequired(input.email);
          return;
        }

        toast.error(result.error.message || result.error.statusText);
        return;
      }

      trackLogin("email");

      const locale = resolveSignInLocale({
        currentLocale,
        userLanguage: result.data?.user.language,
      });
      await getMessagesForRoute(locale).catch((error: unknown) => {
        toast.error(tError("message"));
        throw error;
      });
      await setLocale({ data: locale });
      const currency = normalizeViewerCurrency(result.data?.user.currency);
      if (currency) await setCurrencyPreference({ data: currency });

      await invalidateSession(queryClient);
      await router.invalidate();
      if (result.data?.redirect && result.data.url) {
        window.location.assign(result.data.url);
        return;
      }
      await router.navigate({ href: getRedirectAfterLogin(redirectAfterLogin) });
      toast.success(t("signInSuccess"));
    },
    validators: {
      onSubmit: signInSchema,
    },
  });

  if (isPending) {
    return <Loader />;
  }

  if (verificationRequired) {
    return (
      <div className="space-y-6 text-center">
        <div className="flex justify-center">
          <div className="flex items-center justify-center size-16 rounded-full bg-primary/10">
            <MailIcon className="text-3xl text-primary" />
          </div>
        </div>
        <div className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">{t("verifyEmailTitle")}</h1>
          <p className="text-sm text-muted-foreground">{t("emailNotVerified")}</p>
        </div>
        <Button
          variant="outline"
          onClick={async () => {
            await authClient.sendVerificationEmail({
              email: verificationRequired,
              callbackURL: getRedirectAfterLogin(redirectAfterLogin),
            });
            toast.success(t("verificationResent"));
          }}
        >
          {t("resendVerification")}
        </Button>
        <p className="text-center text-sm text-muted-foreground">
          <button
            type="button"
            onClick={() => setVerificationRequired(null)}
            className="text-primary hover:underline font-medium"
          >
            {t("backToSignIn")}
          </button>
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="text-lg font-semibold tracking-tight">{t("signIn")}</h1>
        <p className="hidden text-sm text-muted-foreground lg:block">{t("signInDescription")}</p>
      </div>

      <AuthAlternativeMethods
        mode="signin"
        redirectAfterLogin={redirectAfterLogin}
        onWhatsAppExpandedChange={setWhatsAppExpanded}
      />

      {!whatsappExpanded && (
        <form.AppForm>
          <form.Form className="space-y-4">
            <form.AppField name="email">
              {(field) => <field.TextField label={t("email")} type="email" />}
            </form.AppField>

            <form.AppField name="password">
              {(field) => (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor={field.name}>{t("password")}</Label>
                    <Link to={"/forgot-password"} className="text-xs text-primary hover:underline">
                      {t("forgotPassword")}
                    </Link>
                  </div>
                  <field.PasswordField />
                </div>
              )}
            </form.AppField>

            <form.SubmitButton className="w-full" size="lg" submittingLabel={t("submitting")}>
              {t("signIn")}
            </form.SubmitButton>
          </form.Form>
        </form.AppForm>
      )}
    </div>
  );
}
